// AC9–AC16, AC24, AC28
// Shared modal for Mark as Resolved / Add Response / Discard
// Title, pre-selection, required-comment flag, and button label vary per action (Appendix B)

import { useState } from 'react';
import { toast } from 'react-toastify';
import { GrievanceCard } from '../services/InstituteAdminGrievanceService';
import InstituteAdminGrievanceService from '../services/InstituteAdminGrievanceService';
import { V2_STATUS_OPTIONS } from '../shared/helpers/statusBadge';

// AC10, AC11, AC12: three action contexts
export type ModalAction = 'markResolved' | 'addResponse' | 'discard';

// Appendix B action matrix
const ACTION_CONFIG: Record<ModalAction, {
    title: string;
    indicator: string;
    preSelectedStatus: string; // '' = none pre-selected
    commentRequired: boolean;
    primaryLabel: string;
    username: string; 
}> = {
    markResolved: {
        title: 'Mark as Resolved',
        indicator: 'Marking as Resolved',
        preSelectedStatus: 'resolved',
        commentRequired: false,
        primaryLabel: 'Mark as Resolved',
        username: '', // Placeholder; replace with actual user context if available
    },
    addResponse: {
        title: 'Add Response',
        indicator: 'Adding Response',
        preSelectedStatus: '',
        commentRequired: true,        // AC13: required for Add Response
        primaryLabel: 'Submit Response',
        username: '', // Placeholder; replace with actual user context if available
    },
    discard: {
        title: 'Discard Grievance',
        indicator: 'Discarding Grievance',
        preSelectedStatus: 'discarded',
        commentRequired: false,
        primaryLabel: 'Discard',
        username: '', // Placeholder; replace with actual user context if available

    },
};

interface Props {
    action: ModalAction;
    grievance: GrievanceCard;
    onSuccess: (grievanceId: number, newStatus: string) => void;
    onClose: () => void;
}

const StatusUpdateModal = ({ action, grievance, onSuccess, onClose }: Props) => {
    const config = ACTION_CONFIG[action];
    
    // AC10, AC11: pre-selected status from action config; AC12: '' = no pre-selection
    const [selectedStatus, setSelectedStatus] = useState(config.preSelectedStatus);
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // AC16: cancel — discard input, close
    const handleClose = () => {
        setSelectedStatus(config.preSelectedStatus);
        setComment('');
        setCommentError('');
        onClose();
    };

    const handleSubmit = async () => {
        // AC13: Add Response requires a comment
        if (config.commentRequired && !comment.trim()) {
            setCommentError('A response comment is required before submitting.');
            return;
        }
        setCommentError('');

        if (!selectedStatus) {
            setCommentError('Please select a status to update.');
            return;
        }

        setIsSubmitting(true);
        try {
            // AC15, AC28: POST to backend — no notification payload
            const result = await InstituteAdminGrievanceService.submitResponse(
                grievance.id,
                selectedStatus,
                comment,
            );
            if (result.success) {
                toast.success(result.message || 'Grievance updated successfully.');
                // AC14: optimistic update — propagate new status to parent
                onSuccess(grievance.id, selectedStatus);
            } else {
                toast.error(result.message || 'Failed to update grievance.');
            }
        } catch {
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered max-600 mx-auto">
                <div className="modal-content radius-15">
                    <div className="modal-body p-4 p-md-5">

                        {/* AC16: (×) close button */}
                        <button
                            type="button"
                            className="btn-close btn-close-small"
                            onClick={handleClose}
                            aria-label="Close"
                        />

                        {/* Modal title — varies per action (Appendix B) */}
                        <h5 className="font-24 font-600 mb-1">{config.title}</h5>
                        <p className="text-muted font-14 mb-4">
                            {config.indicator} for grievance <strong>{grievance.code}</strong>
                        </p>

                        <p>
                            Alumni name: <strong>{grievance.alumni.name} </strong>
                        </p>

                        {/* AC24: Status dropdown — exactly 6 options in canonical order */}
                        <div className="mb-3">
                            <label className="form-label font-14 fw-500">Status</label>
                            <select
                                className="form-select"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                disabled={action === 'markResolved' || action === 'discard'}
                            >
                                <option value="">Select Status</option>
                                {V2_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Comment / Response field */}
                        <div className="mb-2">
                            <label className="form-label font-14 fw-500">
                                Comment / Response
                                {config.commentRequired && <span className="text-danger ms-1">*</span>}
                            </label>
                            <textarea
                                className={`form-control ${commentError ? 'is-invalid' : ''}`}
                                rows={4}
                                placeholder={config.commentRequired ? 'Enter your response…' : 'Optional comments…'}
                                value={comment}
                                onChange={(e) => {
                                    setComment(e.target.value);
                                    if (commentError) setCommentError('');
                                }}
                            />
                            {/* AC13: inline validation error */}
                            {commentError && (
                                <div className="invalid-feedback d-block">{commentError}</div>
                            )}
                        </div>

                        {/* Appendix B: informational note below comment */}
                        <p className="text-muted font-13 mb-4">
                            This response will be recorded in the Activity Timeline for this grievance.
                        </p>

                        {/* Footer buttons */}
                        <div className="d-flex gap-2 justify-content-center">
                            {/* AC16: Cancel */}
                            <button
                                type="button"
                                className="btn btn-border-gray height-40 px-4 pj"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            ><span className="line-height-16">Cancel</span>
                              
                            </button>
                            {/* Primary submit — label varies per action */}
                            <button
                                type="button"
                                className="btn btn-dark-blue height-40 px-4 text-white"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                <span>                    
                                {isSubmitting ? (
                                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                                ) : null}
                                {config.primaryLabel}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusUpdateModal;
